import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface ImageUploadProps {
  label: string;
  value?: string;
  onChange: (imageUrl: string | null) => void;
  entityId: string;
  imageType: 'QUESTION' | 'OPTION' | 'EXPLANATION';
  disabled?: boolean;
  accept?: string;
  maxSize?: number; // in MB
  className?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  value,
  onChange,
  entityId,
  imageType,
  disabled = false,
  accept = 'image/*',
  maxSize = 10,
  className = ''
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(value || null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: `Please select an image smaller than ${maxSize}MB`,
        variant: 'destructive'
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file',
        variant: 'destructive'
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    await uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', imageType);
      formData.append('entityId', entityId);
      formData.append('processImage', 'true');

      const response = await fetch('/api/admin/images/upload', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Tenant-ID': localStorage.getItem('tenantId') || '',
          'X-User-ID': localStorage.getItem('userId') || ''
        }
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      
      if (result.success) {
        onChange(result.imageUrl);
        toast({
          title: 'Image uploaded',
          description: `${label} uploaded successfully`,
        });
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Failed to upload image',
        variant: 'destructive'
      });
      setPreviewUrl(value || null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!value) return;

    try {
      const response = await fetch(`/api/admin/images/${encodeURIComponent(value)}`, {
        method: 'DELETE',
        headers: {
          'X-User-ID': localStorage.getItem('userId') || ''
        }
      });

      if (response.ok) {
        onChange(null);
        setPreviewUrl(null);
        toast({
          title: 'Image removed',
          description: `${label} removed successfully`,
        });
      }
    } catch (error) {
      console.error('Remove error:', error);
      toast({
        title: 'Remove failed',
        description: 'Failed to remove image',
        variant: 'destructive'
      });
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <Label>{label}</Label>
      
      <Card className="border-dashed border-2 hover:border-primary/50 transition-colors">
        <CardContent className="p-4">
          {previewUrl ? (
            <div className="space-y-3">
              <div className="relative">
                <img
                  src={previewUrl}
                  alt={label}
                  className="max-w-full max-h-48 object-contain rounded-md mx-auto"
                />
                {!disabled && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={handleRemove}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              {!disabled && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleClick}
                    disabled={uploading}
                    className="flex-1"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Replace
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div
              className={`flex flex-col items-center justify-center py-8 text-center ${
                disabled ? 'opacity-50' : 'cursor-pointer hover:bg-muted/50'
              }`}
              onClick={handleClick}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-8 w-8 mb-2 animate-spin text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Uploading...</p>
                </>
              ) : (
                <>
                  <ImageIcon className="h-8 w-8 mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Click to upload {label.toLowerCase()}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Max {maxSize}MB • JPG, PNG, GIF, WebP
                  </p>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />
    </div>
  );
};

export default ImageUpload;
